import { useEffect, useRef, useState } from "react";
import { X, Loader2, Send, BadgeCheck, Trash2 } from "lucide-react";
import { api, fileUrl, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";

/** Slide-up comments sheet for a reel. */
export default function CommentSheet({ reel, onClose, onCount }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [items, setItems]   = useState([]);
    const [loading, setLoading] = useState(true);
    const [text, setText]     = useState("");
    const [busy, setBusy]     = useState(false);
    const listRef = useRef(null);

    useEffect(() => {
        api.get(`/reels/${reel.id}/comments`).then((r) => setItems(r.data?.items || []))
            .finally(() => setLoading(false));
    }, [reel.id]);

    const submit = async (e) => {
        e.preventDefault();
        const clean = text.trim();
        if (!clean) return;
        if (!user) {
            navigate(`/login?next=${encodeURIComponent(location.pathname)}`);
            return;
        }
        setBusy(true);
        try {
            const res = await api.post(`/reels/${reel.id}/comments`, { text: clean });
            const c = res.data?.comment;
            if (c) {
                setItems((list) => [c, ...list]);
                onCount?.(1);
                setText("");
                setTimeout(() => listRef.current?.scrollTo({ top: 0, behavior: "smooth" }), 50);
            }
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not post comment");
        } finally {
            setBusy(false);
        }
    };

    const del = async (id) => {
        try {
            await api.delete(`/reels/${reel.id}/comments/${id}`);
            setItems((list) => list.filter((c) => c.id !== id));
            onCount?.(-1);
        } catch { toast.error("Could not delete"); }
    };

    return (
        <div className="fixed inset-0 z-40 flex items-end" style={{ background: "rgba(0,0,0,0.6)" }}
             onClick={onClose} data-testid="reel-comment-sheet">
            <div className="w-full bg-white rounded-t-2xl flex flex-col max-h-[85vh]"
                 onClick={(e) => e.stopPropagation()}>
                {/* Header */}
                <div className="px-4 pt-3 pb-2 border-b flex items-center justify-between"
                     style={{ borderColor: "rgba(0,38,87,0.06)" }}>
                    <div className="font-heading font-extrabold text-[14px]" style={{ color: "#002657" }}>
                        Comments {items.length > 0 && <span className="text-navy-500/60 font-bold">({items.length})</span>}
                    </div>
                    <button onClick={onClose} data-testid="reel-comment-close"
                            className="w-8 h-8 rounded-full flex items-center justify-center"
                            style={{ background: "#f4f4f5" }}>
                        <X className="w-4 h-4" />
                    </button>
                </div>

                {/* List */}
                <div ref={listRef} className="flex-1 overflow-y-auto px-4 py-3">
                    {loading ? (
                        <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin" style={{ color: "#002657" }} /></div>
                    ) : items.length === 0 ? (
                        <div className="text-center py-8 text-[12px]" style={{ color: "rgba(0,38,87,0.55)" }}>
                            Be the first to comment.
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {items.map((c) => (
                                <div key={c.id} className="flex items-start gap-2" data-testid={`reel-comment-${c.id}`}>
                                    <div className="w-8 h-8 rounded-full overflow-hidden shrink-0 flex items-center justify-center text-[11px] font-bold"
                                         style={{ background: "linear-gradient(135deg, #DAA520, #F59E0B)", color: "#001a3d" }}>
                                        {c.author?.avatar_url
                                            ? <img alt="" src={fileUrl(c.author.avatar_url)} className="w-full h-full object-cover" />
                                            : (c.author?.name || "?").slice(0, 1).toUpperCase()}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1">
                                            <div className="font-heading font-extrabold text-[12px]" style={{ color: "#002657" }}>
                                                {c.author?.name || "GPM User"}
                                            </div>
                                            {c.author?.verified && <BadgeCheck className="w-3 h-3" style={{ color: "#F59E0B" }} />}
                                            <div className="text-[9.5px]" style={{ color: "rgba(0,38,87,0.5)" }}>
                                                · {(c.created_at || "").slice(0, 16).replace("T", " ")}
                                            </div>
                                        </div>
                                        <div className="text-[12px] mt-0.5" style={{ color: "#002657" }}>{c.text}</div>
                                    </div>
                                    {user && (user.id === c.user_id || user.id === reel.creator_id || user.role === "admin") && (
                                        <button onClick={() => del(c.id)}
                                                data-testid={`reel-comment-delete-${c.id}`}
                                                className="w-6 h-6 rounded-full flex items-center justify-center"
                                                style={{ background: "#f4f4f5" }}>
                                            <Trash2 className="w-3 h-3" style={{ color: "#DC2626" }} />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Composer */}
                <form onSubmit={submit} className="border-t px-4 py-3 flex items-center gap-2"
                      style={{ borderColor: "rgba(0,38,87,0.06)" }}>
                    <input
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        maxLength={500}
                        placeholder={user ? "Write a comment…" : "Login to comment"}
                        data-testid="reel-comment-input"
                        className="flex-1 h-10 px-3 rounded-full border text-[12.5px]"
                        style={{ borderColor: "rgba(0,38,87,0.15)" }}
                    />
                    <button
                        type="submit"
                        disabled={busy || !text.trim()}
                        data-testid="reel-comment-submit"
                        className="w-10 h-10 rounded-full flex items-center justify-center shadow"
                        style={{
                            background: text.trim() ? "linear-gradient(90deg, #DAA520, #F59E0B)" : "rgba(0,38,87,0.15)",
                            color: "#001a3d",
                        }}
                    >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                    </button>
                </form>
            </div>
        </div>
    );
}
